/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

/* eslint-disable no-duplicate-imports */
import { IComponentProps } from '@msdyn365-commerce/core';
import { ObjectExtensions } from '@msdyn365-commerce-modules/retail-actions';
import React, { ReactNode } from 'react';
import classname from 'classnames';
import { isMobile, Node, VariantType } from '@msdyn365-commerce-modules/utilities';

import { BusinessPartner, BusinessPartnerUser, BusinessPartnersDataActions } from '@msdyn365-commerce/retail-proxy';
import { IDlvryBusinessPartnersResources } from '../dlvry-business-partners.props.autogenerated';
import { DataGrid, IDataGridColumn } from './data-grid';
import { formatCurrencyNOK } from '../../../shared-utils/common-utils';

/**
 * View model of business partner user, used to display.
 */
export interface IBusinessPartnerUserViewModel extends BusinessPartnerUser {
    name: string;
    spendingLimit: ReactNode;
}

/**
 * Props of BusinessPartnerUsersTable.
 */
export interface IBusinessPartnerUsersTableProps extends IComponentProps {
    className: string;
    pageSize?: number;
    businessPartner: BusinessPartner;
    resources: IDlvryBusinessPartnersResources;
    onSelect(businessPartnerUser: BusinessPartnerUser): void;
}

/**
 * BusinessPartnerUsersTable component.
 */
export class BusinessPartnerUsersTable extends React.Component<IBusinessPartnerUsersTableProps> {
    public shouldComponentUpdate(nextProps: Readonly<IBusinessPartnerUsersTableProps>): boolean {
        return this.props.id !== nextProps.id;
    }

    public render(): JSX.Element {
        return (
            <DataGrid
                {...this.props}
                loadData={this.getBusinessPartnerUsers}
                columns={this.getColumns()}
                defaultSorting={{ fieldName: 'Name', ascending: true }}
                searchPlaceholderLabel={this.props.resources.businessPartnerSearchCustomer}
            />
        );
    }

    /**
     * Get column definition of the table.
     * @returns - Columns.
     */
    private readonly getColumns = (): IDataGridColumn<IBusinessPartnerUserViewModel>[] => {
        const isMobileDevice = isMobile({ variant: VariantType.Viewport, context: this.props.context.request }) === 'xs';

        return [
            {
                reactKey: 'accountNumber',
                name: 'ID',
                className: 'business-partner-users-header min-width account-number',
                fieldName: 'AccountNumber',
                isSearchable: true,
                onClick: this.props.onSelect,
                onRender: (data) => (
                    <span>
                        <span className='mobile'>ID:&nbsp;</span>
                        <span>{data.AccountNumber}</span>
                    </span>
                )
            },
            {
                reactKey: 'name',
                name: this.props.resources.businessUserTableNameHeadingText,
                className: 'business-partner-users-header name',
                fieldName: 'name',
                isSearchable: true,
                onClick: this.props.onSelect,
                onRender: (data: IBusinessPartnerUserViewModel) => <a role='button'>{data.name}</a>
            },
            {
                reactKey: 'email',
                name: this.props.resources.businessUserTableEmailHeadingText,
                className: 'business-partner-users-header email',
                fieldName: 'Email',
                onClick: this.props.onSelect,
                onRender: (data) => (
                    <span>
                        <span className='mobile'>{this.props.resources.businessUserTableEmailHeadingText}:&nbsp;</span>
                        <span>{data.Email || '-'}</span>
                    </span>
                )
            },
            {
                reactKey: 'spendingLimit',
                name: this.props.resources.businessUserTableSpendingLimitHeadingText,
                className: `business-partner-users-header spending-limit ${this.props.className}__row__column-with-number`,
                fieldName: 'spendingLimit',
                onClick: !isMobileDevice ? this.props.onSelect : undefined,
                onRender: (data) => (
                    <span>
                        <span className='mobile'>{this.props.resources.businessUserTableSpendingLimitHeadingText}:&nbsp;</span>
                        <span>{data.spendingLimit}</span>
                    </span>
                )
            }
        ];
    };

    /**
     * Call retail proxy to get business partner users.
     * @returns - Business partner users view model.
     */
    private readonly getBusinessPartnerUsers = async (): Promise<IBusinessPartnerUserViewModel[]> => {
        const businessPartners = await BusinessPartnersDataActions.getUsersAsync(
            {
                callerContext: this.props.context.actionContext,
                bypassCache: 'get',
                queryResultSettings: {}
            },
            this.props.businessPartner.BusinessPartnerId
        );
        return businessPartners
            .filter(user => user.StatusValue === 1)
            .map(user => {
                return {
                    ...user,
                    name: `${user.FirstName} ${user.LastName}`,
                    spendingLimit: (
                        <Node className={classname('spending-limit', { zero: !user.SpendingLimit })}>
                            {!ObjectExtensions.isNullOrUndefined(user.SpendingLimit)
                                ? formatCurrencyNOK(user.SpendingLimit)
                                : ''}
                            {!ObjectExtensions.isNullOrUndefined(user.SpendingLimit) && user.SpendingLimit === 0 ? (
                                <div
                                    className='msi-InfoCircle__tooltip'
                                    data-title={`${this.props.resources.businessUserTableSpendingLimitZeroHoverText}`}
                                >
                                    <i className='msi-InfoCircle' aria-hidden='true' />
                                </div>
                            ) : (
                                ''
                            )}
                        </Node>
                    )
                };
            });
    };
}
